/**
 * 时光漫游（design §11.11，P4b）的纯逻辑：挑哪几天停靠（pickStops）、整趟的时间轴（roamTimeline / roamAt）、
 * 每一刻镜头摆哪（roamCamera）。不碰 three.js，好写单元测试；开始、暂停、快进、seek、退出、到站回调都在 galaxy.ts。
 * 路线（每天一个锚点、按弧长插值）在 path.ts，和星轨共用同一条——镜头走的和画出来的是一条路。
 */
import { daysBetween, type LocalDate, type Vec3 } from '@star-sea/shared';
import { groupRuns } from './events.ts';
import { pointAt, type TrailPath } from './path.ts';

// ── 节奏（浏览器里看效果时调这里）──
/** 开场段：镜头从现在的位置飞到起点（opts.from，平时是弧长 0）用这么久 */
const START_S = 1.4;
/** 每站停 2.2 + 0.6·(心动 / 5) 秒：越心动停越久（2.2–2.8） */
const STOP_S = 2.2;
const STOP_SHINE_S = 0.6;
/** 两站之间的飞行时长：弧长差 / CRUISE_V 夹在 0.8–2.4 秒（给了路线时还要照顾转向，见 MAX_YAW） */
const CRUISE_V = 26;
const CRUISE_MIN = 0.8;
const CRUISE_MAX = 2.4;
/** 两站弧长差比这个小就不飞，直接停 */
const NEAR = 0.5;
/** 一趟漫游的时长目标：超过就按同一个比例压缩所有飞行段 */
const TOTAL_S = 75;
/** 压缩飞行段时每段的下限 */
const FLY_MIN = 0.5;
/** 收尾段：最后一站之后沿虚线再走的弧长 */
const END_TAIL = 12;
const END_MIN = 1.5;
const END_MAX = 2.4;
/** 减少动态效果（opts.still）时，飞的段（开场、站间、收尾）都只是这么久的一拍空档：镜头不飞，直接切 */
const STILL_S = 0.6;
/**
 * 镜头转向的速度上限（度/秒）：飞的段至少飞这么久，让转向的峰值不超过它（smoothstep 的峰值是平均的 1.5 倍）。
 * 螺线内圈弯得急，贴着走镜头跟着转，不限的话一小段路也能甩出一百多度每秒
 */
const MAX_YAW = 90;
/**
 * 升空飞越：两站之间的路贴着走要转弯超过 HOP_START 度，就开始改成升起来、直接飞到下一站上空，超过 HOP_FULL 度完全飞越。
 * 一年一圈：隔半年以上的两站，贴着走要转半圈以上，隔几年就是好几圈，镜头会原地打转
 */
const HOP_START = 100;
const HOP_FULL = 170;
/** 飞越至少飞这么久：隔一年的两站在螺线上只差一圈、直飞很近，太快了像跳过去 */
const HOP_MIN_S = 1.4;
/** 飞越最多飞这么久（比 CRUISE_MAX 长）：隔半年的两站朝向正好反过来，要掉头 180°，2.4 秒转不匀 */
const HOP_MAX_S = 3;
/** 飞越时升多高：两头注视点的水平距离 × 0.35，最多 24 个单位；同时往后拉开升高的一半，看得更全 */
const HOP_LIFT = 0.35;
const HOP_LIFT_MAX = 24;
/** 算路上转了多少弯时，每隔这么长的弧长取一次朝向 */
const TURN_STEP = 1;
// ── 镜头 ──
/** 飞行时镜头在彗星后面这么远（弧长）、注视点在彗星前面这么远 */
const CAM_BACK = 5;
const CAM_AHEAD = 6;
/** 镜头离路线的高度：4.5 + sLast / 300，夹在 4.5–8（海越大飞得越高） */
const CAM_H0 = 4.5;
const CAM_H_SPAN = 300;
const CAM_H1 = 8;
/** 停站时镜头离站的水平距离：从 6 慢慢推近到 4.5（Ken Burns 式的视差） */
const STOP_R0 = 6;
const STOP_R1 = 4.5;
/**
 * 停站这段时间里镜头绕站转约 20°，顺着螺线转弯的方向：从飞来的朝向往回偏 10° 转到往前偏 10°。
 * 站间飞行时这 20° 再慢慢转回来，抵掉一点贴着螺线走的转弯——镜头一直朝一个方向匀匀地转，不来回甩
 */
const STOP_TURN = (20 * Math.PI) / 180;
/** 螺线随时间往哪边转：dayAngle 随日期增大，飞的朝向角跟着减小（顺时针），所以是 −1 */
const TURN_DIR = -1;
/** 到站、离站那一刻镜头的朝向比飞的朝向偏多少（停站的开头、结尾） */
const STOP_OFF0 = (-TURN_DIR * STOP_TURN) / 2;
const STOP_OFF1 = (TURN_DIR * STOP_TURN) / 2;
/** 停站时镜头高度是巡航高度的这个倍数 */
const STOP_H = 0.8;
/** 到站前、离站后和飞行姿态混合的窗口（秒），同时不超过那段飞行时长的一半（相邻两站的窗口才不重叠） */
const BLEND_S = 0.6;
/** 收尾段镜头的高度升到巡航高度的几倍（沿虚线升空） */
const END_H = 2;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
/** smoothstep：峰值速度是平均的 1.5 倍（三次的 easeInOut 是 3 倍，沿弯螺线会甩镜头） */
const smooth = (k: number) => k * k * (3 - 2 * k);
const DEG = Math.PI / 180;

// ── 挑站 ──

/** 一个还有照片的日子，打分要用的信息都在上面 */
export interface RoamDay {
  date: LocalDate;
  /** 这天几张（没删的） */
  count: number;
  /** 回忆的心动 1–5 */
  shine: number;
  /** 回忆写了标题 */
  titled: boolean;
  /** 写了故事 */
  story: boolean;
  /** 打了主题 */
  themed: boolean;
  /** 两个人都在这天添过照片 */
  both: boolean;
  /** 这天有双星共鸣的照片（两个人都写了视角） */
  resonance: boolean;
  /** 在一起的纪念日（月日和 since 一样）或者谁的生日 */
  anniversary: boolean;
}

// 打分各项的权重：心动、标题最值钱；这件事的天数每多一天加一点，4 天封顶
const W_SHINE = 2.0;
const W_TITLE = 1.2;
const W_STORY = 0.8;
const W_COUNT = 0.8;
const W_SPAN = 0.4;
const W_THEME = 0.6;
const W_BOTH = 0.6;
const W_RESONANCE = 0.6;
const W_ANNIVERSARY = 0.8;
/** 时间轴按日期四等分，每份配额 floor(max / 4) */
const QUARTERS = 4;

/**
 * 挑停靠站：先把有照片的日子按「相邻的连成一件事」分组（和事件星云同一个分组），每件事最多一站；
 * 再按配额取——时间轴四等分，各份取自己分最高的几件事，不够的顺延给相邻的份。
 * 同样的输入每次挑得一样；返回按日期排好的站。有照片的日子少于两天时返回空（星轨没有实线段，漫游不能开始）。
 */
export function pickStops(days: readonly RoamDay[], max = 16): LocalDate[] {
  const byDate = new Map<LocalDate, RoamDay>();
  for (const d of days) if (!byDate.has(d.date)) byDate.set(d.date, d);
  if (byDate.size < 2) return [];
  const dates = [...byDate.keys()].sort();
  const first = dates[0]!, last = dates[dates.length - 1]!;
  const span = Math.max(1, daysBetween(first, last));
  // 一件事最多一站：取组里分最高的那天，分数一样取早的
  interface Cand {
    date: LocalDate;
    score: number;
  }
  const cands: Cand[][] = Array.from({ length: QUARTERS }, () => []);
  for (const run of groupRuns(dates)) {
    let best = run[0]!, bs = -1;
    for (const date of run) {
      const d = byDate.get(date)!;
      const score =
        (W_SHINE * d.shine) / 5 +
        (d.titled ? W_TITLE : 0) +
        (d.story ? W_STORY : 0) +
        W_COUNT * Math.min(1, d.count / 12) +
        W_SPAN * Math.min(4, run.length - 1) +
        (d.themed ? W_THEME : 0) +
        (d.both ? W_BOTH : 0) +
        (d.resonance ? W_RESONANCE : 0) +
        (d.anniversary ? W_ANNIVERSARY : 0);
      if (score > bs) {
        bs = score;
        best = date;
      }
    }
    // 一件事归它选出的那天所在的那一份
    const q = Math.min(QUARTERS - 1, Math.floor((QUARTERS * daysBetween(first, best)) / span));
    cands[q]!.push({ date: best, score: bs });
  }
  // 每份里分最高的先，分数一样时日期早的先
  const cmp = (a: Cand, b: Cand) => b.score - a.score || (a.date < b.date ? -1 : 1);
  for (const c of cands) c.sort(cmp);
  const quota = Math.floor(max / QUARTERS);
  const chosen: Cand[] = [];
  const take = (q: number) => {
    const c = cands[q]!.shift();
    if (c) chosen.push(c);
    return c;
  };
  // 各份先取自己的配额，记下实际取了几个（候选可能不够）
  const taken = cands.map((c) => Math.min(quota, c.length));
  for (let q = 0; q < QUARTERS; q++) for (let k = 0; k < taken[q]!; k++) take(q);
  // 哪一份不够，缺的名额顺延给别的份：离它近的先（q−1、q+1、q−2、q+2……），距离一样先给早的那份；
  // 各份从自己剩下的候选里按分数取
  for (let q = 0; q < QUARTERS; q++) {
    let left = quota - taken[q]!;
    while (left > 0) {
      let got = false;
      for (let d = 1; d < QUARTERS && !got; d++) {
        for (const j of [q - d, q + d]) {
          if (j >= 0 && j < QUARTERS && cands[j]!.length) {
            take(j);
            left--;
            got = true;
            break;
          }
        }
      }
      if (!got) break;
    }
  }
  // max 不能被 4 整除时，最后剩的名额给全局分最高的剩余候选
  const rest = cands.flat().sort(cmp);
  for (let k = 0; k < max % QUARTERS && k < rest.length; k++) chosen.push(rest[k]!);
  return chosen.map((c) => c.date).sort();
}


// ── 镜头的「机位」──
// 镜头不直接在位置之间插值：注视点和镜头都在动、离得又近时，直接插位置会让朝向突然甩一下。
// 改成描述一个机位——注视点、朝向（水平方向上从镜头看向注视点的角度）、水平距离、高度——在机位之间插值，
// 朝向走最近的那个角度差，转得匀；最后才换算成镜头位置。

interface Rig {
  /** 注视点 */
  x: number;
  y: number;
  z: number;
  /** 从镜头看向注视点的水平朝向：atan2(dx, dz) */
  yaw: number;
  /** 镜头离注视点的水平距离 */
  r: number;
  /** 镜头比注视点高多少 */
  h: number;
}

const rig = (): Rig => ({ x: 0, y: 0, z: 0, yaw: 0, r: 0, h: 0 });
// 每帧都要算：用模块里固定的几块，不在渲染循环里分配
const P: Vec3 = { x: 0, y: 0, z: 0 }, Q: Vec3 = { x: 0, y: 0, z: 0 };
const BASE = rig(), OTHER = rig(), R0 = rig(), R1 = rig();

/** a 转到 b 最近的那个角度差，(−π, π] */
function angleDiff(a: number, b: number): number {
  let d = (b - a) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  else if (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

/** 巡航高度：4.5 + sLast / 300，夹在 4.5–8 */
function camHeight(path: TrailPath): number {
  return clamp(CAM_H0 + path.sLast / CAM_H_SPAN, CAM_H0, CAM_H1);
}

/** 飞的机位：注视彗星前方 CAM_AHEAD 处，镜头在彗星后方 CAM_BACK 处的正上方 hh 高 */
function flightRig(path: TrailPath, s: number, hh: number, o: Rig): Rig {
  pointAt(path, Math.max(0, s - CAM_BACK), P);
  pointAt(path, Math.min(path.total, s + CAM_AHEAD), Q);
  const dx = Q.x - P.x, dz = Q.z - P.z;
  o.x = Q.x;
  o.y = Q.y;
  o.z = Q.z;
  o.yaw = Math.atan2(dx, dz);
  o.r = Math.max(1e-3, Math.hypot(dx, dz));
  o.h = P.y + hh - Q.y;
  return o;
}

/**
 * 停站的机位：注视这一站；朝向从飞到这里时的朝向顺着螺线转 20°（STOP_OFF0 → STOP_OFF1），水平距离 6 推近到 4.5，
 * 高 0.8h，用 smoothstep 走（一点 Ken Burns 式的视差）
 */
function stopRig(path: TrailPath, sa: number, kk: number, h: number, o: Rig): Rig {
  const yaw = flightRig(path, sa, h, o).yaw, e = smooth(kk);
  pointAt(path, sa, P);
  o.x = P.x;
  o.y = P.y;
  o.z = P.z;
  o.yaw = yaw + STOP_OFF0 + (STOP_OFF1 - STOP_OFF0) * e;
  o.r = STOP_R0 + (STOP_R1 - STOP_R0) * e;
  o.h = STOP_H * h;
  return o;
}

/**
 * 升空飞越的机位：从这段起点飞的机位直接插到终点的（朝向走最近的角度差），中间升起来、往后拉开，看得到彗星绕着螺线跑过去。
 * e 是这段的进度（smoothstep 之后），h0、h1 是两头的巡航高度
 */
function hopRig(path: TrailPath, leg: RoamLeg, e: number, h0: number, h1: number, o: Rig): Rig {
  flightRig(path, leg.s0, h0, R0);
  flightRig(path, leg.s1, h1, R1);
  const lift = Math.min(HOP_LIFT_MAX, HOP_LIFT * Math.hypot(R1.x - R0.x, R1.z - R0.z)) * Math.sin(Math.PI * e);
  o.x = R0.x + (R1.x - R0.x) * e;
  o.y = R0.y + (R1.y - R0.y) * e;
  o.z = R0.z + (R1.z - R0.z) * e;
  o.yaw = R0.yaw + angleDiff(R0.yaw, R1.yaw) * e;
  o.r = R0.r + (R1.r - R0.r) * e + lift * 0.5;
  o.h = R0.h + (R1.h - R0.h) * e + lift;
  return o;
}

/** o 往 b 混 w（0–1）；朝向走最近的角度差 */
function mixRig(o: Rig, b: Rig, w: number): void {
  o.x += (b.x - o.x) * w;
  o.y += (b.y - o.y) * w;
  o.z += (b.z - o.z) * w;
  o.yaw += angleDiff(o.yaw, b.yaw) * w;
  o.r += (b.r - o.r) * w;
  o.h += (b.h - o.h) * w;
}

/** 沿路线从 s0 走到 s1，飞的朝向一共转了多少（弧度，各段转角的绝对值相加）：螺线一直往一个方向弯，隔几年就是好几圈 */
function pathTurn(path: TrailPath, s0: number, s1: number): number {
  const n = clamp(Math.ceil((s1 - s0) / TURN_STEP), 1, 4000);
  let prev = flightRig(path, s0, 0, R0).yaw, sum = 0;
  for (let i = 1; i <= n; i++) {
    const yaw = flightRig(path, s0 + ((s1 - s0) * i) / n, 0, R0).yaw;
    sum += Math.abs(angleDiff(prev, yaw));
    prev = yaw;
  }
  return sum;
}

// ── 时间轴 ──

/** 一站停多久（秒）：2.2 + 0.6·心动/5；卡片上照片的慢慢推近也走这么久 */
export function stopHold(shine: number): number {
  return STOP_S + (STOP_SHINE_S * shine) / 5;
}

/** 一段：start 是开场飞进起点，cruise 是站间巡航，stop 是停靠，end 是收尾升空 */
export interface RoamLeg {
  kind: 'start' | 'cruise' | 'stop' | 'end';
  /** 秒 */
  t0: number;
  t1: number;
  /** 这段起点、终点的弧长 */
  s0: number;
  s1: number;
  /** 停的段是第几站；飞的段是要去的那站；start 是 0；end 是 -1 */
  stop: number;
  /** 飞的段（cruise、end）有多少是升空飞越：0 贴着螺线走，1 完全飞越；别的段是 0 */
  hop: number;
}

export interface RoamTimeline {
  legs: RoamLeg[];
  /** 总时长（秒） */
  total: number;
  /** 每站的弧长 */
  stops: number[];
  /** 减少动态效果（opts.still）：飞的段是一拍空档，roamCamera 每一站只摆一个不动的机位 */
  still: boolean;
}

/** roamTimeline 的第五个参数：路线和几个变形开关 */
export interface RoamOptions {
  /** 镜头沿这条路线算（漫游时都给）；不给时只按弧长算节奏（测节奏用） */
  path?: TrailPath;
  /** 从这个弧长出发（默认 0）：星座漫游从第一站开始 */
  from?: number;
  /** 站与站之间都直飞（升空飞越那一套，hop 固定 1）：星座里沿金线走，不贴着螺线绕 */
  direct?: boolean;
  /** 要不要收尾段（默认要）：星座漫游最后一站停完直接结束 */
  end?: boolean;
  /** 减少动态效果：飞的段都是 0.6 秒的空档、不飞越、不按时长上限压缩；停站的机位见 roamCamera */
  still?: boolean;
}

/**
 * 整趟漫游的时间轴：start（1.4 秒，镜头从现在的位置飞进来）→ 依次飞到每站、停下 →
 * end（沿虚线再往前走一段，升空）。总时长超过 75 秒时按同一个比例压缩所有站间飞行（每段不短于 0.5 秒，
 * 停、start、end 不压；压完略超也没关系，不迭代求精确）。
 * 给了 path（漫游时都给）：飞的段还要飞得够久，镜头转向不超过 MAX_YAW；路上转弯太多的改成升空飞越。
 * 不给时只按弧长算（测节奏用）。
 */
export function roamTimeline(stopArcs: readonly number[], shines: readonly number[], sLast: number, total: number, opts: RoamOptions = {}): RoamTimeline {
  const { path, still = false } = opts;
  // 星座漫游从第一站开始（from）；别的漫游从弧长 0
  const from = Math.max(0, opts.from ?? 0);
  interface Draft {
    kind: RoamLeg['kind'];
    dur: number;
    s0: number;
    s1: number;
    stop: number;
    hop: number;
  }
  const yawRate = MAX_YAW * DEG;
  // 飞的段：时长、飞越的比例
  const fly = (kind: 'cruise' | 'end', s0: number, s1: number, stop: number, lo: number, hi: number): Draft => {
    // 减少动态效果：飞的段只是两张卡片之间的一拍空档（镜头停在上一站，roamCamera 管），不飞越
    if (still) return { kind, dur: STILL_S, s0, s1, stop, hop: 0 };
    let dur = (s1 - s0) / CRUISE_V, hop = 0;
    // 星座漫游（direct）：站间直飞（hop 固定 1），不贴着螺线绕；不用算路上转了多少弯
    if (opts.direct && kind === 'cruise') {
      hop = 1;
      if (path) {
        const a = flightRig(path, s0, 0, R0), b = flightRig(path, s1, 0, R1);
        const turn = Math.abs(angleDiff(a.yaw, b.yaw)), dist = Math.hypot(b.x - a.x, b.z - a.z);
        dur = Math.max(dist / CRUISE_V, (1.5 * turn) / yawRate, HOP_MIN_S);
      } else dur = Math.max(dur, HOP_MIN_S);
    } else if (path) {
      const follow = pathTurn(path, s0, s1);
      const a = flightRig(path, s0, 0, R0), b = flightRig(path, s1, 0, R1);
      const turn = Math.abs(angleDiff(a.yaw, b.yaw)), dist = Math.hypot(b.x - a.x, b.z - a.z);
      hop = clamp((follow / DEG - HOP_START) / (HOP_FULL - HOP_START), 0, 1);
      hop = smooth(hop);
      const tFollow = Math.max(dur, (1.5 * follow) / yawRate);
      const tHop = Math.max(dist / CRUISE_V, (1.5 * turn) / yawRate, HOP_MIN_S);
      dur = tFollow + (tHop - tFollow) * hop;
    }
    return { kind, dur: clamp(dur, lo, hi + (Math.max(hi, HOP_MAX_S) - hi) * hop), s0, s1, stop, hop };
  };
  const drafts: Draft[] = [{ kind: 'start', dur: still ? STILL_S : START_S, s0: from, s1: from, stop: 0, hop: 0 }];
  let s = from;
  for (let i = 0; i < stopArcs.length; i++) {
    const a = stopArcs[i]!;
    // 弧长差太小不飞，直接停（两站的镜头姿态在停靠段里直接混，见 roamCamera）
    if (a - s >= NEAR) drafts.push(fly('cruise', s, a, i, CRUISE_MIN, CRUISE_MAX));
    drafts.push({ kind: 'stop', dur: stopHold(shines[i] ?? 3), s0: a, s1: a, stop: i, hop: 0 });
    s = a;
  }
  // 收尾：最后一站之后沿路线继续走（到「未完待续」那头），镜头升空；星座漫游（end:false）停完直接结束
  if (opts.end !== false) drafts.push(fly('end', s, Math.min(total, Math.max(sLast, s) + END_TAIL), -1, END_MIN, END_MAX));
  // 超了上限：所有站间飞行按同一个比例压缩（still 的飞段本来就是下限，不压）
  const flySum = drafts.reduce((v, d) => v + (d.kind === 'cruise' ? d.dur : 0), 0);
  const fixed = drafts.reduce((v, d) => v + d.dur, 0) - flySum;
  if (!still && fixed + flySum > TOTAL_S && flySum > 0) {
    const k = Math.max(0, (TOTAL_S - fixed) / flySum);
    for (const d of drafts) if (d.kind === 'cruise') d.dur = Math.max(FLY_MIN, d.dur * k);
  }
  let t = 0;
  const legs = drafts.map((d) => {
    const leg: RoamLeg = { kind: d.kind, t0: t, t1: t + d.dur, s0: d.s0, s1: d.s1, stop: d.stop, hop: d.hop };
    t += d.dur;
    return leg;
  });
  return { legs, total: t, stops: [...stopArcs], still };
}

/**
 * 时刻 t 漫游在弧长 s 处、第几段（legs 的下标）、这段走了多少（0–1）。
 * 飞的段（巡航、收尾）用 smoothstep 推进：出站平滑加速，到站前、升到顶时减速。
 */
export function roamAt(tl: RoamTimeline, t: number): { s: number; leg: number; k: number } {
  t = clamp(t, 0, tl.total);
  const legs = tl.legs;
  // 段很少（至多几十个），线性找就够：t 落在 [t0, t1) 的那段；正好在结尾取最后一段
  let leg = legs.length - 1;
  for (let i = 0; i < legs.length; i++)
    if (t < legs[i]!.t1) {
      leg = i;
      break;
    }
  const l = legs[leg]!;
  const k = l.t1 > l.t0 ? clamp((t - l.t0) / (l.t1 - l.t0), 0, 1) : 1;
  const e = l.kind === 'cruise' || l.kind === 'end' ? smooth(k) : k;
  return { s: l.s0 + (l.s1 - l.s0) * e, leg, k };
}

// ── 镜头 ──

/**
 * 时刻 t 镜头应该在哪。飞的时候镜头吊在彗星后面 CAM_BACK 个弧长、比路线高 h 处，看着彗星前方 CAM_AHEAD 处；
 * 路上转弯太多的段升空飞越（hop）。停的时候注视这一站，镜头绕站转一点、慢慢推近。停靠前后和飞行姿态平滑混合
 * （窗口 min(0.6, 这段飞的一半)），位置和注视点都不跳——start 段返回它结束那一刻的姿态，和现在镜头的混合由 galaxy.ts 做。
 * 减少动态效果（tl.still）：镜头不飞不绕——每一站停的是绕站推近中间那一刻的固定机位，飞的段（开场、
 * 站间、收尾）就停在上一站上，还没到第一站时停在它跟前；到站、开场、收尾都是直接切。
 */
export function roamCamera(path: TrailPath, tl: RoamTimeline, t: number, out: { pos: Vec3; target: Vec3 }): void {
  const h = camHeight(path);
  const legs = tl.legs;
  const { s, leg, k } = roamAt(tl, t);
  const L = legs[leg]!, pose = BASE;
  if (tl.still) {
    // 飞的段停在上一站的机位；前面没有站（开场、去第一站的巡航）就停在下一站的机位——开场直接切过去
    let at = L.kind === 'stop' ? L.stop : -1;
    if (at < 0) for (let i = leg - 1; i >= 0; i--) if (legs[i]!.kind === 'stop') { at = legs[i]!.stop; break; }
    if (at < 0) for (let i = leg + 1; i < legs.length; i++) if (legs[i]!.kind === 'stop') { at = legs[i]!.stop; break; }
    if (at >= 0) stopRig(path, tl.stops[at]!, 0.5, h, pose);
    else flightRig(path, s, h, pose);
  } else if (L.kind === 'start') {
    // start 段返回它结束那一刻的姿态：下一段是飞就是起点（s1，星座漫游时是第一站）处飞的姿态，是停（第一站就在起点）就是那一站停的开头
    const nx = legs[leg + 1];
    if (nx && nx.kind === 'stop') stopRig(path, tl.stops[nx.stop]!, 0, h, pose);
    else flightRig(path, L.s1, h, pose);
  } else if (L.kind === 'stop') {
    stopRig(path, tl.stops[L.stop]!, k, h, pose);
    // 下一站没有飞的段（弧长差太小）时，这段停的最后一点直接往那一站的开头混
    const nx = legs[leg + 1];
    if (nx && nx.kind === 'stop') {
      const w = Math.min(BLEND_S, (L.t1 - L.t0) / 2);
      if (t > L.t1 - w) mixRig(pose, stopRig(path, tl.stops[nx.stop]!, 0, h, OTHER), smooth((t - (L.t1 - w)) / w));
    }
  } else {
    // cruise 或 end：飞的姿态；收尾段沿路线往前，高度从 h 平滑升到 2h（升空离开）
    const end = L.kind === 'end', e = smooth(k);
    flightRig(path, s, end ? h * (1 + (END_H - 1) * e) : h, pose);
    if (L.hop > 0) mixRig(pose, hopRig(path, L, e, h, end ? h * END_H : h, OTHER), L.hop);
    // 朝向单独算：飞的朝向加上停站转出去的那点偏角，整段慢慢转回来（离站时是 STOP_OFF1，到站时是下一站的 STOP_OFF0）。
    // 不跟着下面的窗口混：窗口里注视点从站上挪到彗星前方、镜头拉远，朝向要是也在两个机位之间插，
    // 会和贴着螺线的转弯叠在一起，离站那一下甩得最快
    const pv = legs[leg - 1], nx = legs[leg + 1];
    const off0 = pv?.kind === 'stop' ? STOP_OFF1 : 0, off1 = nx?.kind === 'stop' ? STOP_OFF0 : 0;
    const yaw = pose.yaw + off0 + (off1 - off0) * e;
    const w = Math.min(BLEND_S, (L.t1 - L.t0) / 2);
    // 离站后的一小段：注视点、距离、高度从那一站停的结尾混回飞的姿态
    if (pv?.kind === 'stop' && t < L.t0 + w) mixRig(pose, stopRig(path, tl.stops[pv.stop]!, 1, h, OTHER), smooth(1 - (t - L.t0) / w));
    // 到站前的一小段：往那一站停的开头混
    if (nx?.kind === 'stop' && t > L.t1 - w) mixRig(pose, stopRig(path, tl.stops[nx.stop]!, 0, h, OTHER), smooth((t - (L.t1 - w)) / w));
    pose.yaw = yaw;
  }
  out.target.x = pose.x;
  out.target.y = pose.y;
  out.target.z = pose.z;
  out.pos.x = pose.x - Math.sin(pose.yaw) * pose.r;
  out.pos.y = pose.y + pose.h;
  out.pos.z = pose.z - Math.cos(pose.yaw) * pose.r;
}
